import { logger } from '@adornis/base/logging.js';
import type { Exam, ExamState } from '../../exam.js';
import { formatTaskReferenceForDisplay } from '../../../shared/grammar-rules-resolution.js';
import { ambiguousTaskRefMessage, resolveRequestedTaskRefs } from './resolve-requested-task-refs.js';

export const refineGradingSchemeToolDef = [
  {
    type: 'function',
    function: {
      name: 'refine_grading_scheme',
      description:
        'Überarbeitet ein spezifisches Bewertungsschema (Grading Scheme / Erwartungshorizont) basierend auf dem Feedback des Nutzers (z.B. "Bei Aufgabe 3 gibt es 10 statt 5 Punkte" oder "Füge einen Punkt für die Einheit hinzu"). Darf aufgerufen werden, wenn bereits ein Schema existiert.',
      parameters: {
        type: 'object',
        properties: {
          taskNumbers: {
            type: 'array',
            items: { type: 'string' },
            description:
              'Liste von Aufgabennummern, deren Bewertungsschemata angepasst werden sollen, als VOLLSTÄNDIGE, punktgetrennte Aufgabennummern von der obersten Ebene an (z.B. ["3.a", "3.b"]). Nennt der Nutzer nur die Bezeichnung einer Teilaufgabe ("a") und kommt diese unter mehreren Aufgaben vor (z.B. 1.a UND 2.a), dann NIEMALS vorsorglich beide eintragen und niemals raten: frage den Nutzer zurück, welche Aufgabe gemeint ist, und rufe dieses Tool erst nach seiner Antwort auf. Kann WEGGELASSEN werden, wenn stattdessen groupNumber oder languageUseSchemeId angegeben wird (der Nutzer nennt direkt eine Gruppe für die sprachliche Leistung statt einer Aufgabe) — die zur Gruppe gehörenden Aufgaben werden dann automatisch verwendet.',
          },
          feedback: {
            type: 'string',
            description: 'Das konkrete Feedback oder die Anweisung des Nutzers zur Anpassung.',
          },
          target: {
            type: 'string',
            enum: ['content', 'languageUse', 'both'],
            description:
              "Worauf sich das Feedback bezieht: 'content' für die inhaltliche Bewertung (Punkte/Kriterien für die Richtigkeit der Antwort — der Standardfall), 'languageUse' wenn es explizit um die Bewertungsregel für die sprachliche Leistung (Grammatik/Rechtschreibung) geht (z.B. \"mach die Regel für die sprachliche Leistung strenger\" oder \"mach die Grammatik-Regel strenger\"), 'both' wenn beides gemeint ist.",
          },
          groupNumber: {
            type: 'integer',
            description:
              'Nur für target languageUse/both: die kleine Gruppennummer ("Gruppe 3"), die der Nutzer genannt hat, oder die eine vorherige Rückfrage dieses Tools zur Auflösung einer Mehrdeutigkeit genannt hat. Wenn der Nutzer eine Gruppe für die sprachliche Leistung DIREKT über ihre Nummer anspricht (z.B. "vergib für Gruppe 3 10 Punkte"), reicht diese Angabe VOLLSTÄNDIG aus — taskNumbers kann dann leer bleiben, die zugehörigen Aufgaben werden automatisch aus der Gruppe übernommen. Niemals raten.',
          },
          languageUseSchemeId: {
            type: 'string',
            description:
              'Fallback für groupNumber, nur wenn eine vorherige Rückfrage dieses Tools genau diese interne ID zur Auswahl genannt hat (Regel ohne Gruppennummer) — genau wie groupNumber reicht dies allein aus, taskNumbers kann leer bleiben. Niemals raten, selbst erzeugen oder dem Nutzer diese ID zeigen.',
          },
        },
        required: ['feedback', 'target'],
      },
    },
  },
];

/**
 * One family's refine, as a row the teacher's turn waits on.
 *
 * `stop` is set when this family's outcome must end the whole tool call — a failure or a timeout on the family
 * that was actually asked for. Under `both` a family failing is not fatal to the other: a content task with no
 * language-use rule to refine is an ordinary outcome, and the content half already landed and must be reported.
 */
async function refineFamily(
  examId: string,
  request: {
    taskRefs: string[];
    feedback: string;
    family: 'content' | 'languageUse';
    fatal: boolean;
    groupNumber?: number;
    languageUseSchemeId?: string;
  },
): Promise<{ stop?: string; refined: boolean; note?: string }> {
  const { taskRefs, feedback, family, fatal, groupNumber, languageUseSchemeId } = request;
  const { dispatchJobsAndWait } = await import('../jobs/dispatch.js');
  const { REFINE_JOB_TYPE } = await import('../jobs/job.js');

  const { outcome, note } = await dispatchJobsAndWait(REFINE_JOB_TYPE.gradingScheme, examId, {
    taskRefs,
    feedback,
    ...(family === 'languageUse' ? { target: 'languageUse' as const } : {}),
    ...(family === 'languageUse' && typeof groupNumber === 'number' ? { groupNumber } : {}),
    ...(family === 'languageUse' && languageUseSchemeId ? { languageUseSchemeId } : {}),
    // The re-grade this refine should set off. Read off the ROW by the grading-scheme run's closure, never
    // dispatched from here — see the header of `handleRefineGradingScheme`.
    regrade: { cause: 'grading_scheme_update', target: family },
  });

  if (outcome === 'failed') {
    return {
      refined: false,
      ...(fatal
        ? {
            stop:
              family === 'languageUse'
                ? 'Fehler: Die Regel für die sprachliche Leistung konnte nicht angepasst werden.'
                : 'Fehler: Das Bewertungsschema konnte nicht angepasst werden.',
          }
        : {}),
    };
  }
  if (outcome === 'pending') {
    return {
      refined: false,
      ...(fatal
        ? {
            stop:
              family === 'languageUse'
                ? 'SYSTEM INFO: Die Anpassung der Regel für die sprachliche Leistung läuft noch im Hintergrund weiter.'
                : 'SYSTEM INFO: Die Anpassung des Bewertungsschemas läuft noch im Hintergrund weiter.',
          }
        : {}),
    };
  }

  // Only the language-use branch leaves a note, and only for a task the teacher NAMED that could not be given
  // a rule at all. Matched on the prefix the handler writes so an ordinary diagnostic note is never shown to a
  // teacher — see `gs-refine.job.ts`.
  return { refined: taskRefs.length > 0, ...(note?.startsWith('HINWEIS:') ? { note } : {}) };
}

/** The tool's arguments, narrowed. `target` falls back to the documented default rather than throwing. */
function readArgs(toolCallArgs: any): {
  target: 'content' | 'languageUse' | 'both';
  taskRefs: string[];
  feedback: string;
  groupNumber?: number;
  languageUseSchemeId?: string;
} {
  const raw = toolCallArgs?.target;
  const rawGroupNumber = Number(toolCallArgs?.groupNumber);
  return {
    target: raw === 'languageUse' || raw === 'both' ? raw : 'content',
    taskRefs: Array.isArray(toolCallArgs?.taskNumbers) ? toolCallArgs.taskNumbers : [],
    feedback: String(toolCallArgs?.feedback ?? ''),
    groupNumber: Number.isFinite(rawGroupNumber) ? rawGroupNumber : undefined,
    languageUseSchemeId:
      typeof toolCallArgs?.languageUseSchemeId === 'string' ? toolCallArgs.languageUseSchemeId : undefined,
  };
}

/** What the agent is told. `hint` is appended verbatim — it is already a finished sentence. */
function resultMessage(refined: boolean, hint?: string): string {
  const base = refined
    ? 'SYSTEM INFO: Das Bewertungsschema wurde angepasst. Die Schülerabgaben werden, sofern sie bereits ' +
      'bewertet wurden, im Hintergrund neu bewertet. Informiere den Nutzer darüber – das Ergebnis der ' +
      'Neubewertung folgt automatisch, sobald es vorliegt.'
    : 'SYSTEM INFO: Bewertungsschema angepasst.';
  return hint ? `${base} ${hint}` : base;
}

/**
 * Natural-language disambiguation for a language-use refine whose requested task belongs to more than one
 * LanguageUseScheme — built ENTIRELY for the LLM, never rendered to the teacher as-is. Leads with "Gruppe N"
 * for a candidate that has a `displayNumber` (the common case going forward — a teacher can just say
 * "Gruppe 3"); falls back to describing the OTHER tasks a candidate covers plus its raw id for a legacy
 * candidate with no number, so mixed old/new exams degrade gracefully instead of showing a blank number.
 *
 * Only the first ambiguous ref is described when several are ambiguous at once — resolving them one at a
 * time avoids widening the tool to a per-ref id/number map for a compound case with no evidence it occurs.
 */
function buildAmbiguityMessage(
  ambiguousRefs: Array<{
    ref: string;
    matchingDependsOn: string;
    candidates: Array<{ _id: string; displayNumber: number | null; maxPoints: number; otherDependsOn: string[] }>;
  }>,
  contentAlreadyRefined: boolean,
): string {
  const parts: string[] = [];
  if (contentAlreadyRefined) parts.push(resultMessage(true));
  if (ambiguousRefs.length > 1) {
    parts.push(
      'SYSTEM INFO: Es gibt mehrere Aufgaben, für die nicht eindeutig ist, welche Regel für die sprachliche Leistung gemeint ist. ' +
        'Kläre sie NACHEINANDER mit dem Nutzer, beginnend mit der ersten.',
    );
  }

  const first = ambiguousRefs[0]!;
  const refDisplay = formatTaskReferenceForDisplay(first.matchingDependsOn);
  const options = first.candidates
    .map((candidate, index) => {
      const otherTasks = candidate.otherDependsOn.length
        ? candidate.otherDependsOn.map(formatTaskReferenceForDisplay).join(', ')
        : 'keine weiteren Aufgaben';
      const label = candidate.displayNumber != null ? `Gruppe ${candidate.displayNumber}` : `Regel ${String.fromCharCode(65 + index)}`;
      const ref = candidate.displayNumber != null ? `groupNumber ${candidate.displayNumber}` : `languageUseSchemeId "${candidate._id}"`;
      return `- ${label} (${ref}): deckt zusätzlich Aufgabe(n) ${otherTasks} ab (aktuell ${candidate.maxPoints} Punkte)`;
    })
    .join('\n');

  parts.push(
    `SYSTEM INFO: Für Aufgabe ${refDisplay} gibt es mehr als eine Bewertungsregel für die sprachliche Leistung — frage den Nutzer ` +
      'AUSSCHLIESSLICH in natürlicher Sprache, welche gemeint ist (z.B. mit der Gruppennummer, falls vorhanden), ' +
      'und nenne dabei NIEMALS eine interne ID. Beschreibe jede Regel über die jeweils ANDEREN Aufgaben, die sie ' +
      `zusätzlich abdeckt:\n${options}\n` +
      'Rufe refine_grading_scheme nach der Antwort erneut auf, mit denselben taskNumbers/feedback/target sowie ' +
      'groupNumber (bevorzugt) oder languageUseSchemeId gesetzt auf die Regel, die der Nutzer gemeint hat.',
  );

  return parts.join(' ');
}

/**
 * ONE MECHANISM FOR BOTH FAMILIES, and `target: 'both'` is TWO ROWS rather than one doing both.
 *
 * Content and language-use are refined by independent engines — content on `GradingScheme.criteria`, language-use
 * always on a `LanguageUseScheme` in `exam.languageUseSchemeRules` (solo or multi-member) — and each needs its own
 * re-grade scope: a language-use-rule edit must not rewrite content grades, and vice versa. A scope travels as the
 * row's own `RegradeIntent`, and one row carries one, so two families means two rows.
 *
 * THIS FILE DISPATCHES NO RE-GRADE. Each intent is dispatched by the row that carries it, when that row completes
 * (`dispatchOwnRegrade` in `gs-refine.job.ts`) — the one place that knows this refine landed and what it was asked
 * to re-grade. Language-use used to be the exception, dispatching its own re-grade inline from a detached async
 * block, for one reason: it had no job, so no row to carry an intent. Behind a row it goes the same way as content.
 *
 * THE REQUESTED REFS ARE THE RIGHT SCOPE for language-use, wider though the change itself can be. A multi-member
 * group is graded as a unit by ONE `languageUseGroupGrading` row per (submission, scheme) — every member task's
 * grading row enqueues the same key and the dedupe index collapses them — and the backfill only touches subtrees
 * of the roots holding the requested refs, the same MAIN tasks grading is dispatched per. The union of refined
 * refs this replaced was broader than it needed to be.
 */
export async function handleRefineGradingScheme(exam: Exam, toolCallArgs: any): Promise<object> {
  const examId = exam._id;
  if (!examId) {
    return { content: 'Fehler: Keine gültige Klausur-ID für die Anpassung des Bewertungsschemas gefunden.' };
  }
  const { target, taskRefs: requestedTaskRefs, feedback, groupNumber, languageUseSchemeId } = readArgs(toolCallArgs);

  // WHICH TASK A TYPED REF MEANS, resolved before any job runs. `gs-generation-agent`'s own `findWithRoot`
  // matches a bare label at ANY depth and keeps the FIRST hit, so "a" on an exam with 1.a and 2.a would
  // silently refine whichever the walk reached first — see `resolveRequestedTaskRefs`. Skipped when the
  // group is named directly: `groupNumber`/`languageUseSchemeId` identify at most one scheme on their own,
  // and its `dependsOn` (already "|"-qualified) replaces these refs below.
  let taskRefs = requestedTaskRefs;
  if (typeof groupNumber !== 'number' && !languageUseSchemeId && taskRefs.length > 0) {
    const { getCollection: getCollectionForRefs } = await import('@adornis/baseql/server/collections.js');
    const { Exam: ExamEntityForRefs } = await import('../../exam.js');
    const examForRefs = await (
      await getCollectionForRefs(ExamEntityForRefs)
    ).findOne({ _id: examId } as any, { projection: { tasks: 1 } } as any);
    const resolved = resolveRequestedTaskRefs((examForRefs as any)?.tasks, taskRefs);
    if (resolved.status === 'ambiguous') {
      logger.warn(
        { examId, ref: resolved.ref, candidates: resolved.candidates, context: 'refineGradingScheme' },
        'Ambiguous task ref — refusing to refine grading scheme',
      );
      return { content: ambiguousTaskRefMessage(resolved.ref, resolved.candidates, 'das Bewertungsschema angepasst') };
    }
    taskRefs = resolved.refs;
  }

  // CONTENT FIRST, AND SEQUENTIALLY, which is why this is two statements rather than a loop over the families.
  // The language-use engine also BACKFILLS a rule for a requested task that has none, and it derives that rule's
  // points as a ratio of the task's CONTENT points (`computeLanguageUseBackfillForRoot`). Running language-use
  // before or beside a content refine that is about to create those points is how a task lands in
  // `blockedNoPoints` for a reason the teacher's own request had already removed.
  // `fatal` is "this family is the whole request", so a failure on it ends the call. Under `both` neither is:
  // one family having nothing to refine must not discard what the other one just did.
  const content =
    target === 'both' || target === 'content'
      ? await refineFamily(examId, { taskRefs, feedback, family: 'content', fatal: target === 'content' })
      : null;
  if (content?.stop) return { content: content.stop };

  // A language-use dispatch would refine EVERY LanguageUseScheme matching a requested ref — a task can belong to
  // more than one, so an ambiguous ref must be resolved (or asked about) BEFORE any job runs, or a scheme
  // the teacher never meant gets silently rewritten too. `exam` here is a minimal tool-context stub (no
  // `languageUseSchemeRules`) — re-fetch the real document, same pattern other tool handlers already use
  // (grading-scheme.tool.ts, master-solution.tool.ts, update-task.tool.ts).
  let languageUseTaskRefs = taskRefs;
  // Reassigned to the resolved scheme's own `_id` once found via direct lookup below — the regrade
  // dispatch (dispatchOwnRegrade in gs-refine.job.ts) and the agent's own defensive resolver both key
  // off `_id`, not `groupNumber`, so a groupNumber-only call must still end up carrying the real id.
  let resolvedLanguageUseSchemeId = languageUseSchemeId;
  if (target === 'languageUse' || target === 'both') {
    const { getCollection } = await import('@adornis/baseql/server/collections.js');
    const { Exam: ExamEntity } = await import('../../exam.js');
    const { resolveLanguageUseSchemesForRequestedRefs } = await import('../agents/gs-generation-agent.js');

    const examCollection = await getCollection(ExamEntity);
    const fullExam = await examCollection.findOne(
      { _id: examId } as any,
      { projection: { languageUseSchemeRules: 1 } } as any,
    );
    const languageUseSchemeRules: any[] = Array.isArray((fullExam as any)?.languageUseSchemeRules)
      ? (fullExam as any).languageUseSchemeRules
      : [];

    if (typeof groupNumber === 'number' || languageUseSchemeId) {
      // The user/LLM already named the group directly (by number or, as a fallback, by a previously
      // stated internal id) — no task number is needed at all, and no ambiguity is possible: a
      // displayNumber/`_id` identifies AT MOST one scheme. Its own `dependsOn` becomes the effective
      // task refs, overriding whatever (if anything) was passed as taskNumbers.
      const scheme = languageUseSchemeRules.find(
        s =>
          (typeof groupNumber === 'number' && Number(s?.displayNumber) === groupNumber) ||
          (languageUseSchemeId && String(s?._id) === languageUseSchemeId),
      );
      if (!scheme) {
        return {
          content:
            `Fehler: ${typeof groupNumber === 'number' ? `Gruppe ${groupNumber}` : 'Die angegebene Regel für die sprachliche Leistung'} wurde im ` +
            'Bewertungsschema nicht gefunden. Bitte die Gruppennummer beim Nutzer noch einmal bestätigen.',
        };
      }
      languageUseTaskRefs = Array.isArray(scheme.dependsOn) ? scheme.dependsOn : [];
      resolvedLanguageUseSchemeId = String(scheme._id);
    } else {
      // No direct group identifier — fall back to resolving by task ref, which can be ambiguous.
      const { ambiguousRefs } = resolveLanguageUseSchemesForRequestedRefs(taskRefs, languageUseSchemeRules, {});
      if (ambiguousRefs.length > 0) {
        return { content: buildAmbiguityMessage(ambiguousRefs, Boolean(content?.refined)) };
      }
    }
  }

  const languageUse =
    target === 'both' || target === 'languageUse'
      ? await refineFamily(examId, {
          taskRefs: languageUseTaskRefs,
          feedback,
          family: 'languageUse',
          fatal: target === 'languageUse',
          groupNumber,
          languageUseSchemeId: resolvedLanguageUseSchemeId,
        })
      : null;
  if (languageUse?.stop) return { content: languageUse.stop };

  return { content: resultMessage(Boolean(content?.refined || languageUse?.refined), languageUse?.note) };
}

export function refineGradingSchemeAvailability(_examState: ExamState): boolean {
  return true;
}
